import { useRef, useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { Accordion, Button, Icon } from '@iwoca/orion';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import {
  requirementsTitle,
  generateCopyStringBody,
  generateBodyTextString,
  requirementsSubTitle,
} from './ShareRequirements.helper';
import styles from './ShareRequirements.module.css';
import { TOfferDataRequirement } from '../../../../types';
import { getAmplitudeDefaultEventProperties } from '../../../../utils/amplitudeEvents';

type TProps = {
  requiredDocs: TOfferDataRequirement['actions'];
  optionalDocs: TOfferDataRequirement['actions'];
};

const ShareRequirements = ({ requiredDocs, optionalDocs }: TProps) => {
  const [isCopyClicked, setIsCopyClicked] = useState(false);
  const requirementListRef = useRef<HTMLDivElement>(null);

  const renderedDocumentRequirements = () => {
    const requiredDocsText = requiredDocs.map((requirement, index) => {
      return (
        <li key={index}>
          <span className={styles.title}>
            {requirementsTitle(requirement.type, requirement.quantifier?.value)}
          </span>
          <p className={styles.subTitle}>
            {requirementsSubTitle(requirement.type)}
          </p>
        </li>
      );
    });

    const optionalDocsText = optionalDocs.map((requirement, index) => {
      return (
        <li key={index}>
          <span className={styles.title}>
            {requirementsTitle(requirement.type, requirement.quantifier?.value)}{' '}
            (optional)
          </span>
          <p className={styles.subTitle}>
            {requirementsSubTitle(requirement.type)}
          </p>
        </li>
      );
    });

    return (
      <>
        <h4 className={styles.requirementsListTitle}>
          What documents do we need?
        </h4>
        {requiredDocsText && <ul>{requiredDocsText}</ul>}
        {optionalDocsText && <ul>{optionalDocsText}</ul>}
      </>
    );
  };

  const clipboardText = () => {
    let textToCopy: string = '';
    if (requiredDocs && optionalDocs) {
      textToCopy = generateCopyStringBody({
        requiredDocs,
        optionalDocs,
      });
    }

    textToCopy =
      textToCopy +
      `\nFiles are PDF or JPG, JPEG, PNG and no more than 2GB` +
      `\n\nIncluding optional documents may increase the chance of approval`;

    return textToCopy;
  };

  const generateMailToLink = () => {
    if (requiredDocs && optionalDocs) {
      const mailtoBody = `${generateBodyTextString({
        docRequirements: requiredDocs,
        newLineBreak: '%0D%0A',
      })}${generateBodyTextString({
        docRequirements: optionalDocs,
        newLineBreak: '%0D%0A',
      })}`;

      return `mailto:?body=Hello %0D%0A %0D%0AI am applying for a loan with iwoca and to verify my trading history I am required to provide several documents. Please can you send me the documents mentioned in the requirement list beneath: %0D%0A %0D%0A${mailtoBody} %0D%0AFiles are PDF or JPG, JPEG, PNG and no more than 2GB %0D%0A %0D%0AIncluding optional documents may increase the chance of approval &subject=Help with documents for new loan`;
    }
  };

  return (
    <Accordion
      frame={false}
      className={styles.accordionContainer}
      title="Need to ask your accountant or someone else for documents?"
    >
      <p className={styles.subTitle}>
        If you don’t have your documents to hand you can share the requirement
        list with someone who does.
      </p>

      <div className={styles.requirementsList} ref={requirementListRef}>
        {renderedDocumentRequirements()}
        <div className={styles.description}>
          <span className={styles.title}>Please make sure...</span>
          <ul>
            <li className={styles.fileSizes}>
              Files are PDF or JPG, JPEG, PNG and no more than 2GB
            </li>
          </ul>
        </div>
        <p className={styles.subTitle}>
          Including optional documents may increase your chance of approval
        </p>
      </div>
      <div className={styles.copyButton}>
        <CopyToClipboard
          text={clipboardText()}
          onCopy={() => {
            tracking.ampli.copyRequiredDocumentsClicked(
              getAmplitudeDefaultEventProperties(),
            );

            setIsCopyClicked(true);
          }}
        >
          <Button variant="tertiary" size="small">
            Copy this list as text
            <Icon icon="copyOutline" />
          </Button>
        </CopyToClipboard>
        {isCopyClicked && (
          <div className={styles.copyButtonSuccess}>
            <Icon icon="checkCircleFilled" className={styles.copyCheckIcon} />
            <p className={styles.copyText}>List copied</p>
          </div>
        )}
      </div>
      <div>
        <Button
          variant="tertiary"
          size="small"
          href={generateMailToLink()}
          onClick={() =>
            tracking.ampli.emailRequiredDocumentsClicked(
              getAmplitudeDefaultEventProperties(),
            )
          }
          className={styles.emailButton}
          target="_blank"
          rel="noreferrer"
        >
          Email this list to someone
          <Icon icon="emailOutline" />
        </Button>
      </div>
    </Accordion>
  );
};

export default ShareRequirements;
